import React, { useEffect, useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import ControlPanelSystem from "src/components/controlPanelSystem/ControlPanelSystem";
import { useCallback } from "react";
import useControlPanel from "src/hooks/useControlPanel";
import { useMemo } from "react";
import Pagination from "src/components/Pagination/Pagination";
import { dataStateType } from "src/constants/status";
import useCurrentActionCache from "src/hooks/useCurrentActionCache";
import { CACHE_HOME_INDEX } from "src/constants/actionCache";
import { compareTwoArrayDnD } from "src/utils/helper";
import { NotifyConfirm } from "src/components/notify/Notify";
import { path } from "src/constants/path";
import {
  createFLFHomeIndex,
  getFLFHomeIndex,
  removeFLFHomeIndex,
  sortFLFHomeIndex,
  updateFLFHomeIndex,
} from "src/apis/FLF/homeIndex.api";
import FLFHomeIndexControlPanel from "../../components/ControlPanel/FLFHomeIndexControlPanel";
import HomeIndexTable from "../../components/Table/HomeIndexTable";

const initHomeIndexData = {
  list: [],
  total_record: 0,
};

const FLFHomeIndex = () => {
  const {
    page,
    searchKey,
    numberPage,
    setNumberPage,
    limit,
    setLimit,
    status,
    setStatus,
    startIndex,
    endIndex,
    setStartDate,
    setEndDate,
    handleInputChange,
    handleChangePage,
  } = useControlPanel();
  const { currentActionCache, setCurrentActionCache } = useCurrentActionCache();
  const [homeIndexData, setHomeIndexData] = useState(initHomeIndexData);
  const [filterHomeIndexs, setFilterHomeIndexs] = useState([]);
  const [listHomeIndexId, setListHomeIndexId] = useState([]);
  const [queueSortApi, setQueueSortApi] = useState([]);
  const [isSorted, setIsSorted] = useState(false);
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [errorsForm, setErrorsForm] = useState({});

  const fetchHomeIndex = useCallback(() => {
    getFLFHomeIndex({}, (result) => {
      setHomeIndexData({ ...result });
      setFilterHomeIndexs([...result.list]);
      setListHomeIndexId([...result.list?.map((c) => c.id)]);
    });
  }, []);

  useEffect(() => {
    let isCancelled = false;
    if (!isCancelled) {
      fetchHomeIndex();
    }

    return () => {
      // cancel the request before component unmounts
      isCancelled = true;
    };
  }, [fetchHomeIndex]);

  useEffect(() => {
    setNumberPage(page);
  }, [page, setNumberPage]);

  const handleOpenPopup = useCallback((isOpen, item = null) => {
    setOpenPopup(isOpen);
    setRecordForEdit(item);
    setErrorsForm({});
  }, []);

  const updateFilteredHomeIndex = useCallback((homeIndexs) => {
    setFilterHomeIndexs(homeIndexs);
    setListHomeIndexId(homeIndexs.map((homeIndex) => homeIndex.id));
  }, []);

  /* ---- handle search ----- */
  const searchByKeyWord = useCallback(
    (homeIndexs, key) => {
      if (status === dataStateType.INACTIVE) {
        return [];
      }
      return homeIndexs.filter((homeIndex) =>
        homeIndex.category?.name
          .toLowerCase()
          .includes(key.toLowerCase().trim())
      );
    },
    [status]
  );

  const handleSearch = useCallback(
    (value) => {
      const filteredHomeIndex = value
        ? searchByKeyWord(homeIndexData.list, value)
        : homeIndexData.list;
      updateFilteredHomeIndex(filteredHomeIndex);
    },
    [homeIndexData.list, searchByKeyWord, updateFilteredHomeIndex]
  );

  useEffect(() => {
    handleSearch(searchKey);
  }, [searchKey, handleSearch]);

  /* ---- handle add new and update ----- */
  const addOrEdit = useCallback(
    (homeIndex, resetForm) => {
      homeIndex.devTypeCodes = homeIndex.devTypeCodes.join(",");
      homeIndex.devSubTypeCodes = homeIndex.devSubTypeCodes.join(",");
      if (homeIndex.id) {
        updateFLFHomeIndex(homeIndex.id, homeIndex, handleApiResponse);
      } else {
        createFLFHomeIndex(homeIndex, handleApiResponse);
      }

      function handleApiResponse(result) {
        if (result.data?.status === 422) {
          setErrorsForm(result.data.data);
        } else {
          fetchHomeIndex();
          resetForm();
          setRecordForEdit(null);
          setOpenPopup(false);
          setErrorsForm({});
          setCurrentActionCache(CACHE_HOME_INDEX);
        }
      }
    },
    [fetchHomeIndex, setCurrentActionCache]
  );

  /* ---- handle sort and DnD ----- */

  const handleSort = useCallback(() => {
    sortFLFHomeIndex({ queueSort: queueSortApi }, () => {
      setQueueSortApi([]);
      setIsSorted(false);
      setCurrentActionCache(CACHE_HOME_INDEX);
      fetchHomeIndex();
    });
  }, [fetchHomeIndex, queueSortApi, setCurrentActionCache]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // kiểm tra nếu không tồn tại over (kéo quá size thì return tránh lỗi)
    if (!over) return;

    if (active.id !== over.id) {
      // lấy vị trí cũ (từ thằng active)
      const oldIndex = filterHomeIndexs.findIndex(
        (homeIndex) => homeIndex.id === active.id
      );

      // lấy vị trí mới (từ thằng order)
      const newIndex = filterHomeIndexs.findIndex(
        (homeIndex) => homeIndex.id === over.id
      );

      const newOrder = arrayMove(filterHomeIndexs, oldIndex, newIndex);
      const newOrderIds = newOrder.map((c) => c.id);

      // check xem có thay đổi thì hiện nút xác nhận cập nhật
      const isOrderChanged = !compareTwoArrayDnD(listHomeIndexId, newOrderIds);
      setFilterHomeIndexs([...newOrder]);

      if (isOrderChanged) {
        setIsSorted(true);
        setQueueSortApi((prev) => [
          ...prev,
          {
            activeHomeIndexId: active.id,
            overHomeIndexId: over.id,
          },
        ]);
      } else {
        setIsSorted(false);
        setQueueSortApi([]);
      }
    }
  };

  const handleDelete = (homeIndexDelete) => {
    NotifyConfirm("warning", "Are you delete it ?", (result) => {
      if (result.isConfirmed) {
        removeFLFHomeIndex(
          homeIndexDelete.id,
          setCurrentActionCache(CACHE_HOME_INDEX)
        );
        setHomeIndexData((prev) => {
          return {
            ...prev,
            list: prev.list.filter(
              (homeIndex) => homeIndex.id !== homeIndexDelete.id
            ),
            total_record: prev.total_record - 1,
          };
        });

        updateFilteredHomeIndex((prev) =>
          prev.filter((homeIndex) => homeIndex.id !== homeIndexDelete.id)
        );
      }
    });
  };

  const resultFilter = useMemo(
    () => searchByKeyWord(filterHomeIndexs, searchKey),
    [filterHomeIndexs, searchByKeyWord, searchKey]
  );
  const listItems = useMemo(
    () => resultFilter.slice(startIndex, endIndex),
    [resultFilter, startIndex, endIndex]
  );

  return (
    <div>
      <div className="page-header">
        <h2>Home Index</h2>
      </div>

      {/** control pan */}
      <FLFHomeIndexControlPanel
        handleOpenPopup={handleOpenPopup}
        isSorted={isSorted}
        handleSort={handleSort}
        currentActionCache={currentActionCache}
        recordForEdit={recordForEdit}
        openPopup={openPopup}
        setOpenPopup={setOpenPopup}
        addOrEdit={addOrEdit}
        errorsForm={errorsForm}
      >
        <ControlPanelSystem
          searchKey={searchKey}
          handleInputChange={(e) => handleInputChange(e, handleSearch)}
          limit={limit}
          setLimit={setLimit}
          setNumberPage={setNumberPage}
          status={status}
          setStatus={setStatus}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          isDisplayDatePicker={false}
        />
      </FLFHomeIndexControlPanel>

      {/** table */}
      <HomeIndexTable
        data={listItems}
        handleOpenPopup={handleOpenPopup}
        handleDelete={handleDelete}
        status={dataStateType.ACTIVE}
        onDragEnd={handleDragEnd}
      />

      {/** Pagination */}
      <Pagination
        pathname={path.flfHomeIndex}
        page={numberPage}
        handleChangePage={(page) => handleChangePage(page)}
        limit={limit}
        pageSize={resultFilter.length}
      />
    </div>
  );
};

export default FLFHomeIndex;
